// @ts-nocheck
import { Card, Code, Divider, SimpleGrid, Stack, Text, Title } from '@/components/ui';
import { DashboardWidget, DataSummary, ReportBuilder } from '@/components/advanced/reporting';
import {
  IconCurrencyDollar,
  IconUsers,
  IconShoppingCart,
  IconReceipt,
  IconChartBar,
} from '@tabler/icons-react';

export default function ReportingPage() {
  // DataSummary mock items
  const summaryItems = [
    {
      key: 'revenue',
      label: 'Total Revenue',
      value: 128400,
      change: 12.5,
      format: 'currency',
      icon: <IconCurrencyDollar size={16} />,
      color: 'green',
      description: 'vs last month',
    },
    {
      key: 'orders',
      label: 'Orders',
      value: 1842,
      change: 8.1,
      format: 'number',
      icon: <IconShoppingCart size={16} />,
      color: 'blue',
      description: 'vs last month',
    },
    {
      key: 'customers',
      label: 'New Customers',
      value: 312,
      change: -3.4,
      format: 'number',
      icon: <IconUsers size={16} />,
      color: 'violet',
      description: 'vs last month',
    },
    {
      key: 'refunds',
      label: 'Refunds',
      value: 4200,
      change: -1.2,
      format: 'currency',
      icon: <IconReceipt size={16} />,
      color: 'orange',
      description: 'vs last month',
    },
  ];

  // ReportBuilder available fields
  const reportFields = [
    { key: 'name', label: 'Customer Name', type: 'text' },
    { key: 'email', label: 'Email', type: 'text' },
    { key: 'amount', label: 'Order Amount', type: 'number', aggregations: ['sum', 'avg', 'count'] },
    { key: 'status', label: 'Status', type: 'select', options: [
      { value: 'paid', label: 'Paid' },
      { value: 'pending', label: 'Pending' },
      { value: 'refunded', label: 'Refunded' },
    ] },
    { key: 'createdAt', label: 'Created Date', type: 'date' },
  ];

  // ReportBuilder preview rows
  const previewRows = [
    { name: 'Acme Corp', email: 'billing@acme.io', amount: 4200, status: 'paid', createdAt: '2024-01-10' },
    { name: 'Globex Ltd', email: 'ap@globex.com', amount: 1850, status: 'pending', createdAt: '2024-01-12' },
    { name: 'Initech', email: 'finance@initech.co', amount: 980, status: 'paid', createdAt: '2024-01-14' },
  ];

  const savedReports = [
    { id: 'r1', name: 'Monthly Revenue' },
    { id: 'r2', name: 'Pending Invoices' },
  ];

  return (
    <Stack gap="xl">
      <Stack gap="xs">
        <Title order={2}>Reporting Components</Title>
        <Text c="dimmed" size="sm">
          Components for building reports, summarizing metrics, and composing analytics dashboards
        </Text>
      </Stack>

      {/* Dashboard Widget */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>DashboardWidget</Title>
            <Text size="sm" c="dimmed">
              Compact KPI card with value, trend badge, sparkline, and optional progress bar
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example:</Text>
            <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
              <DashboardWidget
                title="Monthly Revenue"
                value="$128,400"
                change={12.5}
                changePeriod="last month"
                icon={<IconCurrencyDollar size={18} />}
                iconColor="green"
                sparklineData={[20, 35, 28, 45, 40, 60, 72]}
                lastUpdated={new Date('2024-01-15T09:30:00')}
                onRefresh={() => {}}
                onExport={() => {}}
              />
              <DashboardWidget
                title="Active Users"
                value="3,124"
                change={-2.8}
                changePeriod="last week"
                icon={<IconUsers size={18} />}
                iconColor="blue"
                sparklineData={[80, 72, 75, 60, 64, 58, 55]}
              />
              <DashboardWidget
                title="Goal Completion"
                value="68%"
                icon={<IconChartBar size={18} />}
                iconColor="violet"
                progress={68}
                progressLabel="Quarterly target"
              />
            </SimpleGrid>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<DashboardWidget
  title="Monthly Revenue"
  value="$128,400"
  change={12.5}
  changePeriod="last month"
  icon={<IconCurrencyDollar size={18} />}
  iconColor="green"
  sparklineData={[20, 35, 28, 45, 40, 60, 72]}
  lastUpdated={new Date()}
  onRefresh={() => {}}
  onExport={() => {}}
/>

// Progress variant
<DashboardWidget
  title="Goal Completion"
  value="68%"
  icon={<IconChartBar size={18} />}
  iconColor="violet"
  progress={68}
  progressLabel="Quarterly target"
/>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • title - Widget label (required)
              <br />
              • value - Primary metric to display
              <br />
              • change - Percentage change (drives trend badge color)
              <br />
              • icon / iconColor - Leading themed icon
              <br />
              • sparklineData - Array of numbers rendered as an inline sparkline
              <br />
              • progress / progressLabel - Optional progress bar
              <br />
              • variant - 'stat' | 'chart' | 'list' | 'custom'
              <br />
              • onRefresh / onExpand / onSettings / onExport - Menu actions
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* Data Summary */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>DataSummary</Title>
            <Text size="sm" c="dimmed">
              Grid of summary metrics with formatted values and change indicators
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example (cards variant):</Text>
            <DataSummary
              title="Sales Overview"
              description="Key metrics for the current period"
              items={summaryItems}
              columns={4}
            />
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example (list variant):</Text>
            <DataSummary items={summaryItems} variant="list" />
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`const items = [
  {
    key: 'revenue',
    label: 'Total Revenue',
    value: 128400,
    change: 12.5,
    format: 'currency',
    icon: <IconCurrencyDollar size={16} />,
    color: 'green',
    description: 'vs last month',
  },
  {
    key: 'orders',
    label: 'Orders',
    value: 1842,
    change: 8.1,
    format: 'number',
    icon: <IconShoppingCart size={16} />,
    color: 'blue',
  },
  // ... more items
];

<DataSummary
  title="Sales Overview"
  description="Key metrics for the current period"
  items={items}
  columns={4}
/>

// Alternate layout
<DataSummary items={items} variant="list" />`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Item Interface:</Text>
            <Code block language="typescript">
{`interface SummaryItem {
  key: string;
  label: string;
  value: string | number;
  previousValue?: number;
  change?: number;
  icon?: ReactNode;
  color?: string;
  format?: 'number' | 'currency' | 'percentage' | 'custom';
  description?: string;
}`}
            </Code>
          </Stack>
        </Stack>
      </Card>

      {/* Report Builder */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>ReportBuilder</Title>
            <Text size="sm" c="dimmed">
              Interactive builder to pick fields, filters, sorting, grouping, and visualization for a report
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example:</Text>
            <ReportBuilder
              availableFields={reportFields}
              initialConfig={{ name: 'Customer Orders', fields: ['name', 'amount', 'status'] }}
              previewData={previewRows}
              savedReports={savedReports}
              onConfigChange={() => {}}
              onGenerate={() => {}}
              onSave={() => {}}
              onExport={() => {}}
              onLoadReport={() => {}}
            />
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`const availableFields = [
  { key: 'name', label: 'Customer Name', type: 'text' },
  { key: 'amount', label: 'Order Amount', type: 'number',
    aggregations: ['sum', 'avg', 'count'] },
  { key: 'status', label: 'Status', type: 'select', options: [
    { value: 'paid', label: 'Paid' },
    { value: 'pending', label: 'Pending' },
  ] },
  { key: 'createdAt', label: 'Created Date', type: 'date' },
];

const previewData = [
  { name: 'Acme Corp', amount: 4200, status: 'paid', createdAt: '2024-01-10' },
  // ... more rows
];

<ReportBuilder
  availableFields={availableFields}
  initialConfig={{ name: 'Customer Orders', fields: ['name', 'amount', 'status'] }}
  previewData={previewData}
  savedReports={[{ id: 'r1', name: 'Monthly Revenue' }]}
  onConfigChange={(config) => { /* ... */ }}
  onGenerate={(config) => { /* ... */ }}
  onSave={(config) => { /* ... */ }}
  onExport={(format) => { /* 'csv' | 'excel' | 'pdf' */ }}
  onLoadReport={(id) => { /* ... */ }}
/>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Field Interface:</Text>
            <Code block language="typescript">
{`interface ReportField {
  key: string;
  label: string;
  type: 'text' | 'number' | 'date' | 'boolean' | 'select';
  options?: { value: string; label: string }[];
  aggregations?: ('sum' | 'avg' | 'count' | 'min' | 'max')[];
}`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • availableFields - Fields the user can add to the report (required)
              <br />
              • initialConfig - Partial starting configuration
              <br />
              • previewData - Sample rows shown in the preview table
              <br />
              • savedReports - Loadable saved report list
              <br />
              • onConfigChange / onGenerate / onSave / onExport / onLoadReport - Handlers
            </Text>
          </Stack>
        </Stack>
      </Card>

      <Divider />

      {/* Use Cases */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Common Use Cases</Title>

          <SimpleGrid cols={{ base: 1, md: 3 }} spacing="md">
            <Stack gap="xs">
              <Text size="sm" fw={500}>Executive Dashboards</Text>
              <Text size="xs" c="dimmed">
                Combine DashboardWidget cards into a top-line KPI grid with trends and sparklines for at-a-glance health.
              </Text>
            </Stack>

            <Stack gap="xs">
              <Text size="sm" fw={500}>Metric Summaries</Text>
              <Text size="xs" c="dimmed">
                Use DataSummary to present grouped financial or operational metrics with consistent formatting and deltas.
              </Text>
            </Stack>

            <Stack gap="xs">
              <Text size="sm" fw={500}>Ad-hoc Reporting</Text>
              <Text size="xs" c="dimmed">
                Let users assemble custom reports with ReportBuilder, then preview, save, and export to CSV, Excel, or PDF.
              </Text>
            </Stack>
          </SimpleGrid>
        </Stack>
      </Card>
    </Stack>
  );
}
